import { useCallback, useEffect, useRef, useState } from "react";
import { fetchWatch, stallOverride } from "../lib/api";
import { Circle, Pause, Play } from "lucide-react";

const POLL_MS = 3000;

const Tile = ({ k, v, sub, testid }) => (
  <div className="border border-[var(--line)] px-4 py-3" data-testid={testid}>
    <p className="label">{k}</p>
    <p className="mt-1.5 font-data text-lg tabular-nums leading-none text-zinc-100">{v}</p>
    {sub && <p className="mt-1.5 font-data text-[10px] text-zinc-600">{sub}</p>}
  </div>
);

const clock = (s) => {
  const h = Math.floor((s || 0) / 3600);
  const m = Math.floor(((s || 0) % 3600) / 60);
  return `${h}h ${String(m).padStart(2, "0")}m`;
};

/** Follow one subject's interview turn by turn, polled every 3s. */
export const LiveWatch = ({ subject }) => {
  const [head, setHead] = useState(null);
  const [turns, setTurns] = useState([]);
  const [following, setFollowing] = useState(true);
  const [err, setErr] = useState("");
  const since = useRef(-1);
  const current = useRef(subject);
  const scroller = useRef(null);

  const poll = useCallback(async () => {
    const asked = subject;
    try {
      const d = await fetchWatch(asked, since.current);
      // a poll fired for the previous subject must not land in this subject's feed
      if (current.current !== asked) return;
      setErr("");
      setHead(d);
      if (d.turns.length) {
        const fresh = d.turns.filter((t) => t.seq > since.current);
        if (!fresh.length) return;
        since.current = fresh[fresh.length - 1].seq;
        setTurns((prev) => [...prev, ...fresh]);
      }
    } catch (e) {
      if (current.current !== asked) return;
      setErr(e?.response?.data?.detail || "watch feed unavailable");
    }
  }, [subject]);

  useEffect(() => {
    current.current = subject;
    since.current = -1;
    setTurns([]);
    setHead(null);
  }, [subject]);

  useEffect(() => {
    if (!following) return;
    poll();
    const t = setInterval(poll, POLL_MS);
    return () => clearInterval(t);
  }, [poll, following]);

  useEffect(() => {
    const el = scroller.current;
    if (el && following) el.scrollTop = el.scrollHeight;
  }, [turns, following]);

  const st = head?.stall;

  return (
    <div data-testid="admin-live-watch" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="label">
          Live watch · {head?.name || "—"} · polled every {POLL_MS / 1000}s
        </p>
        <div className="flex items-center gap-3">
          <span
            data-testid="watch-live-indicator"
            className="flex items-center gap-2 font-data text-[10px] uppercase tracking-[0.18em] text-zinc-500"
          >
            <Circle
              size={7}
              className={following ? "fill-zinc-100 text-zinc-100" : "text-zinc-700"}
            />
            {following ? "live" : "frozen"}
          </span>
          <button
            data-testid="watch-toggle-btn"
            onClick={() => setFollowing((f) => !f)}
            className="btn-stark flex items-center gap-2 !px-3 !py-2"
          >
            {following ? <Pause size={11} strokeWidth={1.8} /> : <Play size={11} strokeWidth={1.8} />}
            {following ? "Freeze" : "Resume"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-px bg-[var(--line)] sm:grid-cols-3 lg:grid-cols-6">
        <Tile
          testid="watch-status"
          k="Status"
          v={
            head?.awaiting_reply
              ? "THINKING"
              : st?.stalled
                ? "STALLED"
                : st?.typing
                  ? "TYPING"
                  : st?.awaiting_answer
                    ? "ANSWERING"
                    : "IDLE"
          }
          sub={st?.online ? "tab open" : "tab closed"}
        />
        <Tile
          k="Silence"
          v={`${st?.silence_seconds ?? 0}s`}
          sub={`threshold ${st?.threshold_seconds ?? 45}s`}
        />
        <Tile k="Confidence" v={`${head?.metrics?.predictive_confidence ?? 0}%`} sub={`gate ${head?.metrics?.gate_open ? "OPEN" : "shut"}`} />
        <Tile k="Honesty" v={Math.round(head?.metrics?.honesty_signal ?? 0)} sub={head?.metrics?.honesty_trend} />
        <Tile
          k="Coverage"
          v={`${head?.coverage?.filled ?? 0}/${head?.coverage?.total ?? 46}`}
          sub={`phase ${head?.phase ?? "P0"}`}
        />
        <Tile
          k="Clock"
          v={clock(head?.timer?.elapsed_seconds)}
          sub={`${head?.timer?.state ?? "—"} · of ${clock(head?.timer?.window_seconds)}`}
        />
      </div>

      {st?.stalled && (
        <div className="flex flex-wrap items-center gap-2 border border-zinc-500 px-4 py-3">
          <span className="font-data text-[11px] uppercase tracking-[0.18em] text-zinc-100">
            stalled {st.silence_seconds}s
          </span>
          {["nudge", "rephrase", "skip"].map((mode) => (
            <button
              key={mode}
              data-testid={`watch-stall-${mode}`}
              onClick={() => stallOverride(head.subject, mode).then(poll)}
              className="btn-stark !px-3 !py-2"
            >
              {mode}
            </button>
          ))}
        </div>
      )}

      <div
        ref={scroller}
        data-testid="watch-feed"
        className="thin-scroll max-h-[32rem] space-y-5 overflow-y-auto border border-[var(--line)] p-5"
      >
        {turns.map((t) => (
          <div key={`${t.seq}`} data-testid={`watch-turn-${t.seq}`}>
            <p className="label">
              #{t.seq} {t.role === "user" ? "subject" : "cartographer"} ·{" "}
              {t.created_at?.slice(11, 19)}
              {t.masked && <span className="ml-3 !text-zinc-300">withheld by subject</span>}
              {t.role === "assistant" && t.confidence !== null && t.confidence !== undefined && (
                <span className="ml-3 !text-zinc-400">
                  C {t.confidence}%
                  {t.confidence_delta !== null && t.confidence_delta !== undefined && (
                    <span className="ml-1 text-zinc-600">
                      {t.confidence_delta > 0 ? "+" : ""}
                      {t.confidence_delta}
                    </span>
                  )}
                </span>
              )}
              {t.control_type && (
                <span className="ml-3 !text-zinc-500">
                  {t.control_type} · {t.control_result || "—"}
                </span>
              )}
            </p>
            <p
              className={`mt-2 whitespace-pre-wrap text-sm leading-relaxed ${
                t.masked ? "italic text-zinc-600" : t.role === "user" ? "text-zinc-100" : "text-zinc-400"
              }`}
            >
              {t.content}
            </p>
            {t.categories?.length > 0 && (
              <p className="mt-1.5 font-data text-[10px] text-zinc-700">
                touched: {t.categories.join(" · ")}
              </p>
            )}
          </div>
        ))}
        {turns.length === 0 && (
          <p className="font-data text-[11px] text-zinc-600">
            {err || "Nothing on the wire yet for this subject."}
          </p>
        )}
      </div>

      {head?.awaiting_reply && (
        <p className="font-data text-[11px] text-zinc-500">
          the Cartographer is composing its reply…
        </p>
      )}
    </div>
  );
};

export default LiveWatch;
